/**
 * A custom element that manages the mobile dropdown menu.
 * 
 * This menu appears as a dropdown under the header (not a slide-in drawer).
 * Features:
 * - Multiple accordions can be open simultaneously
 * - Icon swap (+ ↔ −) via aria-expanded
 * - Close on outside click
 * - 2-level menu structure only
 * 
 * @extends HTMLElement
 */
class MobileMenu extends HTMLElement {
  /** @type {HTMLElement | null} */
  hamburger;
  
  /** @type {NodeListOf<HTMLElement>} */
  toggleButtons;
  /**
   * Called when the element is connected to the DOM
   * @returns {void}
   */
  connectedCallback() {
    this.hamburger = document.querySelector('.mobile-menu-hamburger');
    this.toggleButtons = this.querySelectorAll('.mobile-menu__toggle');
    
    if (!this.hamburger) return;

    this.hamburger.addEventListener('click', () => this.toggleMenu());
    
    this.toggleButtons.forEach(button => {
      button.addEventListener('click', () => this.toggleAccordion(button));
    });
    
    document.addEventListener('click', (e) => this.handleOutsideClick(e));
    
    this.addEventListener('keyup', this.#onKeyUp);
    window.addEventListener('resize', this.#onResize);
  }

  /**
   * Called when the element is disconnected from the DOM
   * @returns {void}
   */
  disconnectedCallback() {
    this.removeEventListener('keyup', this.#onKeyUp);
    window.removeEventListener('resize', this.#onResize);
  }

  /**
   * Close the menu when Escape key is pressed
   * @param {KeyboardEvent} event
   * @returns {void}
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;
    
    if (!this.hidden) {
      this.toggleMenu();
    }
  };

  /**
   * Handle window resize to cleanup body overflow when menu is hidden
   * @returns {void}
   */
  #onResize = () => {
    if (!this.hamburger) return;
    
    const isMenuOpen = this.hamburger.getAttribute('aria-expanded') === 'true';
    const isHamburgerVisible = this.hamburger.offsetParent !== null;
    
    if (isMenuOpen && !isHamburgerVisible) {
      document.body.style.overflow = '';
      this.hamburger.setAttribute('aria-expanded', 'false');
      this.hidden = true;
      this.closeAllAccordions();
    }
  };

  /**
   * Toggle the entire mobile menu open/closed
   * @returns {void}
   */
  toggleMenu() {
    if (!this.hamburger) return;
    
    const isExpanded = this.hamburger.getAttribute('aria-expanded') === 'true';
    const newState = !isExpanded;
    
    this.hamburger.setAttribute('aria-expanded', String(newState));
    this.hidden = !newState;
    
    if (newState) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      this.closeAllAccordions();
    }
  }

  /**
   * Toggle an individual accordion item
   * Multiple accordions can be open at the same time
   * @param {HTMLElement} button
   * @returns {void}
   */
  toggleAccordion(button) {
    const isExpanded = button.getAttribute('aria-expanded') === 'true';
    const submenuId = button.getAttribute('aria-controls');
    
    if (!submenuId) return;
    
    const submenu = document.getElementById(submenuId);
    if (!submenu) return;
    
    const newState = !isExpanded;
    button.setAttribute('aria-expanded', String(newState));
    submenu.hidden = !newState;
  }

  /**
   * Close all accordions
   * @returns {void}
   */
  closeAllAccordions() {
    this.toggleButtons.forEach(button => {
      const submenuId = button.getAttribute('aria-controls');
      
      if (!submenuId) return;
      
      const submenu = document.getElementById(submenuId);
      if (submenu) {
        button.setAttribute('aria-expanded', 'false');
        submenu.hidden = true;
      }
    });
  }

  /**
   * Close the menu when clicking outside
   * @param {MouseEvent} e
   * @returns {void}
   */
  handleOutsideClick(e) {
    if (!this.hamburger || !(e.target instanceof Node)) return;
    
    const isMenuOpen = this.hamburger.getAttribute('aria-expanded') === 'true';
    
    if (isMenuOpen && !this.contains(e.target) && !this.hamburger.contains(e.target)) {
      this.toggleMenu();
    }
  }
}

if (!customElements.get('mobile-menu')) {
  customElements.define('mobile-menu', MobileMenu);
}
